import React, { useMemo } from 'react';
import DatePicker from 'react-datepicker';
import { shift } from '@floating-ui/react';
import 'react-datepicker/dist/react-datepicker.css';
import './index.scss';
import Select from 'react-select';
import { getDateFromString } from '../../../utility';

const months = Array.from({ length: 12 }).map((_, i) => new Date(0, i).toLocaleString('en', { month: 'long' }));

const monthOptions = months.map((month, index) => ({
  value: index,
  label: month,
}));

// The picker is used for birth dates as often as for upcoming ones, so the year
// dropdown spans a full lifetime back and a couple of decades ahead by default.
const DEFAULT_START_YEAR = 1910;
const DEFAULT_YEARS_RANGE = new Date().getFullYear() + 20 - DEFAULT_START_YEAR + 1;

// With `portalPopper` the popper renders into this node on <body> instead of
// next to the input. Form fields are absolutely positioned with their own
// z-index, so each one is a stacking context: a popper left inside its field is
// painted over by every field below it no matter how high its own z-index goes.
// Opt-in because a body-level popper sits under modal overlays and outside any
// page selector that styles the calendar.
const POPPER_PORTAL_ID = 'date-calendar-popper-portal';
// react-datepicker only flips the popper vertically; without shift a field
// near the edge of the screen has its calendar cut off by the viewport.
const POPPER_MODIFIERS = [shift({ padding: 15 })];

export default function DateCalendar({
  startDate,
  endDate,
  handleChange,
  selectedDates = [],
  selectedDate = null,
  minDate = null,
  maxDate = null,
  isClearable = false,
  selectsRange = false,
  id = null,
  placeholderText = 'Select...',
  readOnly = false,
  className = 'form-control',
  onFocus = () => {},
  filterDate = null,
  onInputClick = () => {},
  popperProps = null,
  disabled = false,
  selectsMultiple = false,
  shouldCloseOnSelect = true,
  customInput = null,
  withPortal = false,
  startYear = DEFAULT_START_YEAR,
  yearsRange = DEFAULT_YEARS_RANGE,
  wrapperClassName = '',
  portalPopper = false,
}) {
  const yearOptions = useMemo(() => {
    const years = Array.from({ length: yearsRange }).map((_, i) => startYear + i);

    return years.map((year) => ({
      value: year,
      label: year.toString(),
    }));
  }, [startYear, yearsRange]);

  return (
    <div className="date-range-calendar">
      <DatePicker
        id={id}
        shouldCloseOnSelect={shouldCloseOnSelect}
        selectsMultiple={selectsMultiple}
        disabled={disabled}
        selectedDates={selectedDates}
        isClearable={isClearable}
        readOnly={readOnly}
        todayButton="Today"
        filterDate={filterDate}
        onInputClick={onInputClick}
        popperProps={popperProps}
        portalId={portalPopper ? POPPER_PORTAL_ID : undefined}
        popperClassName="date-range-calendar-popper"
        popperModifiers={POPPER_MODIFIERS}
        selectsRange={selectsRange}
        wrapperClassName={wrapperClassName}
        selected={selectedDate}
        startDate={startDate}
        endDate={endDate}
        maxDate={maxDate}
        onFocus={onFocus}
        minDate={minDate}
        className={className}
        dateFormat="MM/dd/yyyy"
        disabledKeyboardNavigation
        placeholderText={placeholderText}
        onChange={handleChange}
        customInput={customInput}
        renderCustomHeader={({
          date,
          changeYear,
          changeMonth,
          decreaseMonth,
          increaseMonth,
          prevMonthButtonDisabled,
          nextMonthButtonDisabled,
        }) => (
          <div className="custom-datepicker-header">
            <div className="custom-datepicker-selects">
              <div className="custom-datepicker-month-select">
                <Select
                  options={monthOptions}
                  className="form-control-select"
                  classNamePrefix="react-select"
                  value={monthOptions.find(
                    (option) => option.value === (typeof date === 'string' ? getDateFromString(date).getMonth() : date.getMonth()),
                  )}
                  onChange={(selectedOption) => {
                    if (selectedOption) {
                      changeMonth(selectedOption.value);
                    }
                  }}
                  isSearchable={false}
                />
              </div>

              <Select
                options={yearOptions}
                className="form-control-select"
                classNamePrefix="react-select"
                value={yearOptions.find(
                  (option) => option.value === (typeof date === 'string' ? getDateFromString(date).getFullYear() : date.getFullYear()),
                )}
                onChange={(selectedOption) => {
                  if (selectedOption) {
                    changeYear(selectedOption.value);
                  }
                }}
                isSearchable={false}
              />
            </div>

            <div className="custom-datepicker-arrows">
              <button onClick={decreaseMonth} disabled={prevMonthButtonDisabled} type="button">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M15 18L9 12L15 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </button>
              <button onClick={increaseMonth} disabled={nextMonthButtonDisabled} type="button">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M9 6L15 12L9 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </button>
            </div>
          </div>
        )}
      />
    </div>
  );
}
